import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, ArrowLeft, Sparkles, Globe, Lock, DollarSign, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import Header from "@/components/Header";

export default function AIDatingChat() {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [character, setCharacter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [giftPanelOpen, setGiftPanelOpen] = useState(false);
  const [giftCatalog, setGiftCatalog] = useState([]);
  const [coinBalance, setCoinBalance] = useState(null);
  const [sendingGift, setSendingGift] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [cRes, mRes] = await Promise.all([
        fetch(`${API}/ai-dating/characters/${characterId}`, { credentials: "include" }),
        fetch(`${API}/ai-dating/characters/${characterId}/messages`, { credentials: "include" }),
      ]);
      if (cRes.status === 403) {
        const err = await cRes.json().catch(() => ({}));
        if (err?.detail?.code === "kyc_required") {
          navigate("/kyc");
          return;
        }
        toast.error("Access denied");
        navigate("/ai-dating");
        return;
      }
      if (cRes.status === 404) {
        toast.error("Character not found");
        navigate("/ai-dating");
        return;
      }
      if (cRes.ok) setCharacter(await cRes.json());
      if (mRes.ok) setMessages(await mRes.json());
    } catch (e) {
      toast.error("Failed to load chat");
    } finally {
      setLoading(false);
    }
  }, [characterId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    // optimistic user message
    const optimisticId = `tmp_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { message_id: optimisticId, role: "user", content: text, created_at: new Date().toISOString() },
    ]);
    setInput("");

    try {
      const res = await fetch(`${API}/ai-dating/characters/${characterId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: text }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 403 && err?.detail?.code === "kyc_required") {
          navigate("/kyc");
          return;
        }
        toast.error(err.detail || "Failed to send");
        // roll back optimistic
        setMessages((prev) => prev.filter((m) => m.message_id !== optimisticId));
        return;
      }
      const { user_message, ai_message } = await res.json();
      setMessages((prev) =>
        prev.filter((m) => m.message_id !== optimisticId).concat([user_message, ai_message])
      );
    } catch (e) {
      toast.error("Failed to send");
      setMessages((prev) => prev.filter((m) => m.message_id !== optimisticId));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openGiftPanel = async () => {
    setGiftPanelOpen(true);
    if (giftCatalog.length === 0) {
      try {
        const res = await fetch(`${API}/gifts/catalog`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setGiftCatalog(data.gifts || []);
        }
      } catch { /* catalog is optional */ }
    }
    try {
      const r = await fetch(`${API}/coins/balance`, { credentials: "include" });
      if (r.ok) setCoinBalance((await r.json()).coin_balance ?? 0);
    } catch { /* balance optional */ }
  };

  const handleSendGift = async (gift) => {
    if (!character?.owner_user_id || character.owner_user_id === user?.user_id) {
      toast.error("Can't gift yourself!");
      return;
    }
    if (coinBalance !== null && coinBalance < gift.coins) {
      toast.error(`Need ${gift.coins} coins, you have ${coinBalance}`);
      return;
    }
    setSendingGift(true);
    try {
      const res = await fetch(`${API}/gifts/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          gift_id: gift.id,
          to_user_id: character.owner_user_id,
          ai_character_id: character.character_id,
        }),
      });
      if (res.ok) {
        toast.success(`${gift.emoji} ${gift.label} sent!`);
        // Optimistic in-chat marker
        setMessages((prev) => [
          ...prev,
          {
            message_id: `gift_${Date.now()}`,
            role: "user",
            content: `${gift.emoji} sent a ${gift.label}!`,
            created_at: new Date().toISOString(),
          },
        ]);
        setCoinBalance((b) => (b !== null ? Math.max(0, b - gift.coins) : b));
        setGiftPanelOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 403 && err?.detail?.code === "kyc_required") {
          toast.error("KYC required to send gifts");
        } else if (res.status === 402) {
          toast.error(err.detail || "Not enough coins");
        } else {
          toast.error(err.detail || "Failed to send gift");
        }
      }
    } catch {
      toast.error("Failed to send gift");
    } finally {
      setSendingGift(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000033] text-[#FF66FF]">
        <Header />
        <div className="container mx-auto px-4 py-10 text-center">Loading…</div>
      </div>
    );
  }
  if (!character) return null;

  const avatar = character.avatar_url
    ? character.avatar_url.startsWith("http")
      ? character.avatar_url
      : `${process.env.REACT_APP_BACKEND_URL}${character.avatar_url}`
    : null;

  return (
    <div className="min-h-screen bg-[#000033] text-[#FF66FF] flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-4 max-w-2xl flex-1 flex flex-col">
        <button
          onClick={() => navigate("/ai-dating")}
          className="myspace-btn text-xs mb-3 flex items-center gap-1 w-fit"
          data-testid="ai-chat-back-btn"
        >
          <ArrowLeft className="w-3 h-3" />
          Back
        </button>

        {/* Character header */}
        <div className="brutal-card p-3 mb-3 flex items-center gap-3" data-testid="ai-chat-header">
          <div
            className="w-12 h-12 border-2 border-[#FF00FF] bg-[#000033] overflow-hidden flex-shrink-0"
            style={{ boxShadow: "0 0 10px rgba(255,0,255,0.5)" }}
          >
            {avatar ? (
              <img src={avatar} alt={character.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#FF00FF]">
                <Sparkles className="w-6 h-6" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-[#00FFFF] truncate" data-testid="ai-chat-name">
                {character.name}
              </h2>
              {character.visibility === "public" ? (
                <Globe className="w-3 h-3 text-[#00FF00]" />
              ) : (
                <Lock className="w-3 h-3 text-[#FF66FF]" />
              )}
            </div>
            <p className="text-xs text-[#FF66FF] truncate">{character.personality}</p>
          </div>
          {character.owner_user_id && character.owner_user_id !== user?.user_id && (
            <>
              <button
                onClick={openGiftPanel}
                className="myspace-btn text-xs flex items-center gap-1 px-2 py-1 text-[#FF66FF]"
                data-testid="ai-chat-gift-btn"
                title="Send a gift"
              >
                <Gift className="w-3 h-3" />
                Gift
              </button>
              <button
                onClick={() =>
                  navigate(
                    `/tip/${character.owner_user_id}?ai=${encodeURIComponent(character.name)}&ai_id=${encodeURIComponent(character.character_id)}`
                  )
                }
                className="myspace-btn text-xs flex items-center gap-1 px-2 py-1 text-[#FFD700]"
                data-testid="ai-chat-tip-btn"
                title="Tip the creator"
              >
                <DollarSign className="w-3 h-3" />
                Tip
              </button>
            </>
          )}
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="brutal-card flex-1 overflow-y-auto p-3 space-y-2 min-h-[300px] max-h-[60vh]"
          data-testid="ai-chat-messages"
        >
          {messages.length === 0 && (
            <div className="text-center text-xs text-[#888] py-6">
              Say hi to {character.name} 💌
            </div>
          )}
          {messages.map((m) => (
            <motion.div
              key={m.message_id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  m.role === "user"
                    ? "bg-[rgba(255,0,255,0.15)] border-2 border-[#FF00FF] text-[#FFFFFF]"
                    : "bg-[rgba(0,255,255,0.10)] border-2 border-[#00FFFF] text-[#FFFFFF]"
                }`}
                data-testid={`ai-chat-msg-${m.role}`}
              >
                {m.content}
              </div>
            </motion.div>
          ))}
          {sending && (
            <div className="text-xs text-[#00FFFF] flex items-center gap-1" data-testid="ai-chat-typing">
              <Sparkles className="w-3 h-3 animate-pulse" />
              {character.name} is typing…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="mt-3 flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={`Message ${character.name}...`}
            maxLength={2000}
            className="flex-1 bg-[#000022] border-2 border-[#6600CC] px-2 py-2 text-[#FFFFFF] resize-none"
            data-testid="ai-chat-input"
          />
          <button
            onClick={handleSend}
            disabled={sending || !input.trim()}
            className="myspace-btn px-3 py-2 disabled:opacity-50 flex items-center gap-1"
            data-testid="ai-chat-send-btn"
          >
            <Send className="w-4 h-4" />
            Send
          </button>
        </div>
      </div>

      {/* Gift Panel */}
      {giftPanelOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-3"
          onClick={() => setGiftPanelOpen(false)}
          data-testid="ai-chat-gift-panel"
        >
          <div
            className="brutal-card bg-[#000033] p-4 w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#00FFFF] flex items-center gap-1">
                <Gift className="w-4 h-4" /> Send a gift to {character.name}
              </h3>
              <button
                onClick={() => setGiftPanelOpen(false)}
                className="text-[#FF66FF] text-lg leading-none"
                data-testid="ai-chat-gift-close"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {coinBalance !== null && (
              <p className="text-xs text-[#FFD700] mb-2" data-testid="ai-chat-gift-balance">
                🪙 Your balance: <b>{coinBalance}</b> coins
              </p>
            )}
            {giftCatalog.length === 0 ? (
              <p className="text-xs text-[#FF66FF]">Loading gifts…</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {giftCatalog.map((gift) => {
                  const affordable = coinBalance === null || coinBalance >= gift.coins;
                  return (
                    <button
                      key={gift.id}
                      onClick={() => handleSendGift(gift)}
                      disabled={sendingGift || !affordable}
                      className={`brutal-card p-2 text-center transition ${
                        affordable ? "hover:bg-[rgba(255,0,255,0.15)]" : "opacity-40 cursor-not-allowed"
                      }`}
                      data-testid={`ai-chat-gift-item-${gift.id}`}
                    >
                      <div className="text-3xl">{gift.emoji}</div>
                      <div className="text-[10px] font-bold text-[#FFFFFF] truncate">{gift.label}</div>
                      <div className="text-[10px] text-[#FFD700]">🪙 {gift.coins}</div>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-[#888] mt-3">
              Gifts send coins to the character's creator. They keep the majority; a small platform fee applies.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
